import { z } from 'zod';
import {
  idSchema,
  isoDateTimeSchema,
  moneySchema,
  paginationQuerySchema,
  timeOfDaySchema,
} from './common';
import { userPublicSchema } from './users';

/** The 9 notification events (DESIGN 4.18.3), in the brief's order. */
export const NOTIFICATION_TYPES = [
  'group_added',
  'expense_added',
  'expense_edited',
  'payment_to_confirm',
  'payment_confirmed',
  'payment_rejected',
  'placeholder_linked',
  'weekly_reminder',
  'invite_redeemed',
] as const;
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

/**
 * Push switches in Settings (per account). Several events share one switch:
 * `expenses` = expense_added + expense_edited, `your_payments` = payment_confirmed +
 * payment_rejected. The inbox always receives everything.
 */
export const PUSH_PREFERENCE_KEYS = [
  'group_added',
  'expenses',
  'payment_to_confirm',
  'your_payments',
  'placeholder_linked',
  'weekly_reminder',
  'invite_redeemed',
] as const;
export const pushPreferenceKeySchema = z.enum(PUSH_PREFERENCE_KEYS);
export type PushPreferenceKey = z.infer<typeof pushPreferenceKeySchema>;

export const PUSH_PREFERENCE_FOR_TYPE: Readonly<Record<NotificationType, PushPreferenceKey>> = {
  group_added: 'group_added',
  expense_added: 'expenses',
  expense_edited: 'expenses',
  payment_to_confirm: 'payment_to_confirm',
  payment_confirmed: 'your_payments',
  payment_rejected: 'your_payments',
  placeholder_linked: 'placeholder_linked',
  weekly_reminder: 'weekly_reminder',
  invite_redeemed: 'invite_redeemed',
};

/** Defaults: everything on except the weekly reminder (opt-in). */
export const DEFAULT_PUSH_PREFERENCES: Readonly<Record<PushPreferenceKey, boolean>> = {
  group_added: true,
  expenses: true,
  payment_to_confirm: true,
  your_payments: true,
  placeholder_linked: true,
  weekly_reminder: false,
  invite_redeemed: true,
};

/**
 * Events that ignore per-group mute (money you may need to act on, invites). `invite_redeemed`
 * goes to the invite's creator (any member) and to every admin; the name is kept as is.
 */
export const MUTE_EXEMPT_TYPES: readonly NotificationType[] = [
  'payment_to_confirm',
  'payment_confirmed',
  'payment_rejected',
  'invite_redeemed',
];

/** Delivery channels. `email` is reserved (not implemented in v1). */
export const NOTIFICATION_CHANNELS = ['inbox', 'push', 'email'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/** Inbox retention. */
export const NOTIFICATION_RETENTION_DAYS = 90;
/** Expenses added by the same person in the same group within this window collapse. */
export const NOTIFICATION_COLLAPSE_MINUTES = 5;

export const notificationResolutionSchema = z.enum(['confirmed', 'rejected', 'cancelled']);
export type NotificationResolution = z.infer<typeof notificationResolutionSchema>;

/**
 * An inbox row. `title` and `body` are rendered on the server in the recipient's language
 * when the notification is created (never retroactive, DESIGN assumption 19); `data` has
 * the structured values (ids, amounts) if the client prefers to render itself.
 */
export const notificationSchema = z.object({
  id: idSchema,
  type: notificationTypeSchema,
  groupId: idSchema.nullable(),
  actor: userPublicSchema.nullable(),
  title: z.string(),
  /** The meta line ("€58.60 · you borrowed €14.65 · Lisbon, October"), may be empty. */
  body: z.string(),
  /** In-app deep link, e.g. `/expenses/abc`. */
  url: z.string(),
  data: z.record(z.string(), z.unknown()),
  /** > 1 when several expenses collapsed into this row. */
  count: z.int(),
  read: z.boolean(),
  /** In a group the user muted (shown with a bell-off icon; never counts in the badge). */
  muted: z.boolean(),
  /** `payment_to_confirm` that is still waiting for the user (show Confirm / Not received). */
  actionable: z.boolean(),
  /** For `payment_to_confirm`: how it was resolved (on any device), else `null`. */
  resolution: notificationResolutionSchema.nullable(),
  /** Settlement id for payment events. */
  settlementId: idSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Notification = z.infer<typeof notificationSchema>;

/**
 * `data` of the payment events (`payment_to_confirm`, `payment_confirmed`, `payment_rejected`).
 * Rows created before `groupName`/`method` were added to every event may lack them.
 */
export const paymentNotificationDataSchema = z.object({
  settlementId: idSchema,
  groupId: idSchema,
  /** Group name when the event happened, so resolved rows can still show it. */
  groupName: z.string().optional(),
  amount: moneySchema,
  method: z.string().optional(),
  /** `payment_to_confirm` only: who recorded it relative to the recipient. */
  direction: z.enum(['paid_you', 'you_paid']).optional(),
});
export type PaymentNotificationData = z.infer<typeof paymentNotificationDataSchema>;

export const notificationListQuerySchema = paginationQuerySchema.extend({
  /** `actionable` = the Home "Needs your attention" card. */
  filter: z.enum(['all', 'unread', 'actionable']).optional(),
});
export type NotificationListQuery = z.infer<typeof notificationListQuerySchema>;

export const notificationListSchema = z.object({
  items: z.array(notificationSchema),
  nextCursor: z.string().nullable(),
  unreadCount: z.int(),
});
export type NotificationList = z.infer<typeof notificationListSchema>;

/** Badge count: unread, excluding muted groups (mute-exempt events still count). */
export const unreadCountSchema = z.object({ unreadCount: z.int() });
export type UnreadCount = z.infer<typeof unreadCountSchema>;

export const quietHoursSchema = z.object({
  enabled: z.boolean(),
  /** Local time in the user's timezone; overnight ranges (22:00 → 08:00) are supported. */
  start: timeOfDaySchema,
  end: timeOfDaySchema,
});
export type QuietHours = z.infer<typeof quietHoursSchema>;

export const notificationPreferencesSchema = z.object({
  push: z.record(pushPreferenceKeySchema, z.boolean()),
  quietHours: quietHoursSchema,
  /** IANA zone used for quiet hours and the weekly reminder (same as the profile timezone). */
  timezone: z.string(),
  mutedGroups: z.array(z.object({ id: idSchema, name: z.string(), emoji: z.string().nullable() })),
});
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

/** `PATCH /api/notifications/preferences`: partial; `push` merges key by key. */
export const updateNotificationPreferencesRequestSchema = z.object({
  push: z.partialRecord(pushPreferenceKeySchema, z.boolean()).optional(),
  quietHours: quietHoursSchema.partial().optional(),
  timezone: z.string().min(1).max(64).optional(),
});
export type UpdateNotificationPreferencesRequest = z.input<
  typeof updateNotificationPreferencesRequestSchema
>;

// ---------------------------------------------------------------------------
// Web Push
// ---------------------------------------------------------------------------

/** `GET /api/push/vapid-public-key`: use as `applicationServerKey` in `pushManager.subscribe`. */
export const vapidPublicKeySchema = z.object({ publicKey: z.string() });
export type VapidPublicKey = z.infer<typeof vapidPublicKeySchema>;

/**
 * `POST /api/push/subscriptions`: `PushSubscription.toJSON()` as is. Bound to the current
 * device session (the push switch is per device). Re-posting replaces the device's previous one.
 */
export const pushSubscriptionRequestSchema = z.object({
  endpoint: z.url({ protocol: /^https$/ }).max(2048),
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    /**
     * Uncompressed P-256 point, base64url: 65 bytes starting with 0x04, i.e. 87 characters
     * (88 padded) starting with `B` + one of `A`–`P`.
     */
    p256dh: z
      .string()
      .regex(/^B[A-P][A-Za-z0-9_-]{85}=?$/, 'Expected a 65-byte uncompressed P-256 key'),
    /** 16-byte auth secret, base64url: 22 characters (24 padded). */
    auth: z.string().regex(/^[A-Za-z0-9_-]{22}(==)?$/, 'Expected a 16-byte auth secret'),
  }),
});
export type PushSubscriptionRequest = z.input<typeof pushSubscriptionRequestSchema>;

/** `DELETE /api/push/subscriptions`: turn push off on this device. */
export const pushUnsubscribeRequestSchema = z.object({ endpoint: z.string().max(2048).optional() });
export type PushUnsubscribeRequest = z.input<typeof pushUnsubscribeRequestSchema>;

export const pushStatusSchema = z.object({
  /** This device (session) has a subscription on the server. */
  subscribed: z.boolean(),
  /** Push is configured on the server (VAPID keys present). */
  available: z.boolean(),
});
export type PushStatus = z.infer<typeof pushStatusSchema>;

/**
 * JSON payload inside every push message (decrypt is done by the browser; read it in the
 * service worker's `push` event with `event.data.json()`).
 * Show it with `registration.showNotification(title, { body, tag, data: { url }, actions })`.
 * `silent: true` (test push) means: do not show anything.
 */
export const pushPayloadSchema = z.object({
  v: z.literal(1),
  notificationId: idSchema.nullable(),
  type: z.union([notificationTypeSchema, z.literal('test')]),
  title: z.string(),
  body: z.string(),
  /** Event type + group id, so repeats replace each other. */
  tag: z.string(),
  url: z.string(),
  silent: z.boolean().optional(),
  /** For `payment_to_confirm` where the platform supports notification actions. */
  actions: z
    .array(z.object({ action: z.enum(['confirm', 'reject']), title: z.string() }))
    .optional(),
  settlementId: idSchema.optional(),
});
export type PushPayload = z.infer<typeof pushPayloadSchema>;
